import React, { useState } from "react"
import { Button, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPalette } from "@fortawesome/free-solid-svg-icons"
import TableStyleDrawer from "shared/ui/TableStyle/TableStyleDrawer.component"
import { describeTableStyle } from "shared/ui/Table/style/looks"
import { DEFAULT_TABLE_STYLE_CHOICE } from "types/tableStyle.types"
import { useTableStyleStore } from "stores/tableStyleStore"

/**
 * Opens the Customise drawer for the Query Console's table style (one choice
 * per user, see tableStyleStore). "icon" sits in an output tab's toolbar,
 * "button" beside "Export all" in the Builder.
 *
 * The icon variant carries its button role on a wrapping span:
 * FontAwesomeIcon always renders `role="img"` and `aria-hidden`, whatever
 * props it is given.
 */
const ConsoleTableStyleButton: React.FC<{ variant?: "icon" | "button" }> = ({
  variant = "icon",
}) => {
  const [open, setOpen] = useState(false)
  const choice = useTableStyleStore((s) => s.consoleChoice)
  const setChoice = useTableStyleStore((s) => s.setConsoleChoice)
  const tooltip = `Table style: ${describeTableStyle(choice)}`
  return (
    <>
      <Tooltip title={tooltip}>
        {variant === "icon" ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Table style"
            className="inline-flex cursor-pointer"
            onClick={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                setOpen(true)
              }
            }}
          >
            <FontAwesomeIcon
              icon={faPalette}
              style={{ color: "var(--dq-brand)" }}
            />
          </span>
        ) : (
          <span>
            <Button
              aria-label="Table style"
              icon={<FontAwesomeIcon icon={faPalette} />}
              onClick={() => setOpen(true)}
            >
              Style
            </Button>
          </span>
        )}
      </Tooltip>
      <TableStyleDrawer
        open={open}
        title="Table style: Query Console"
        value={choice}
        fallback={DEFAULT_TABLE_STYLE_CHOICE}
        onChange={setChoice}
        onClose={() => setOpen(false)}
      />
    </>
  )
}

export default ConsoleTableStyleButton
